/**
 * shopifyApi.ts — full Shopify API surface re-exported for lazy pages.
 *
 * Eagerly-rendered modules (Header, MegaMenu, CartContext) import directly
 * from shopifyApi.eager.ts — NOT from here — so the module-level Map
 * initialisation below stays out of the initial JS bundle.
 *
 * This file's `_productCache` and all the customer/checkout/address functions
 * are only ever imported by lazy page components.
 */

// Re-export everything from the eager split so pages can keep their
// existing `from '@/lib/shopifyApi'` imports unchanged.
export * from './shopifyApi.eager';
export { getCachedProduct, seedCachedProduct, type ShopifyProductFull, type ShopifyVariantFull } from './productCache';
import { getCachedProduct, seedCachedProduct, normalizeProduct, type ShopifyProductFull } from './productCache';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
import { fetchCatalogResource } from './catalogDataClient';
import { fetchAllCollections, fetchAllCollectionProducts } from './shopifyApi.eager';

/**
 * Shopify rich-text metafields store content as a JSON AST.
 * This utility extracts plain text from that AST.
 * Falls back to the raw string if it is not valid JSON.
 */
export function parseShortDescription(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const ast = JSON.parse(value) as { type: string; value?: string; children?: unknown[] };
    const texts: string[] = [];
    function walk(node: { type: string; value?: string; children?: unknown[] }) {
      if (node.type === 'text' && typeof node.value === 'string') {
        const t = node.value.trim();
        if (t) texts.push(t);
      }
      if (Array.isArray(node.children)) {
        (node.children as typeof ast[]).forEach(walk);
      }
    }
    walk(ast);
    return texts.join(' ') || null;
  } catch {
    return value.trim() || null;
  }
}

export async function searchProducts(q: string, limit = 8): Promise<import('./shopifyApi.eager').ShopifyProduct[]> {
  if (!q.trim()) return [];
  const data = await fetchCatalogResource<{ products: import('./shopifyApi.eager').ShopifyProduct[] }>(
    `search:${q.trim()}:${limit}`,
    `${BASE}/api/shopify/search?q=${encodeURIComponent(q)}&limit=${limit}`,
  );
  return data.products;
}

/**
 * Fetch products filtered by Shopify tags (AND-combined).
 */
export async function fetchProductsByTags(
  tags: string[],
  limit = 12,
): Promise<import('./shopifyApi.eager').ShopifyProduct[]> {
  if (tags.length === 0) {
    const { fetchProducts } = await import('./shopifyApi.eager');
    return fetchProducts(limit);
  }
  const params = new URLSearchParams({
    tags: tags.join(","),
    limit: String(limit),
  });
  const data = await fetchCatalogResource<{ products: import('./shopifyApi.eager').ShopifyProduct[] }>(
    `tags:${tags.join(",")}:${limit}`,
    `${BASE}/api/shopify/products/recommended?${params}`,
  );
  return data.products;
}

export async function fetchProductByHandle(
  handle: string,
): Promise<ShopifyProductFull | null> {
  const data = await fetchCatalogResource<{ product: ShopifyProductFull } | null>(
    `product:${handle}`,
    `${BASE}/api/shopify/products/${encodeURIComponent(handle)}`,
    { onNotFound: () => null },
  );
  const product = data?.product ? normalizeProduct(data.product) : null;
  if (product) seedCachedProduct(product);
  return product;
}

/**
 * Resolve a customer-facing collection by its exact Shopify title, then
 * retrieve every product published to that collection. Collection handles can
 * change independently of the title, so add-on categories should use this
 * helper instead of hard-coding handles.
 */
export async function fetchProductsFromCollectionTitle(
  title: string,
): Promise<import('./shopifyApi.eager').ShopifyProduct[]> {
  const normalizedTitle = title.trim().toLocaleLowerCase('pl-PL');
  if (!normalizedTitle) return [];

  const collections = await getAllCollectionsCached();
  const collection = collections.find(
    candidate => candidate.title.trim().toLocaleLowerCase('pl-PL') === normalizedTitle,
  );
  if (!collection) return [];

  return fetchAllCollectionProducts(collection.handle);
}

let _collectionsPromise: ReturnType<typeof fetchAllCollections> | null = null;

function getAllCollectionsCached(): ReturnType<typeof fetchAllCollections> {
  if (!_collectionsPromise) {
    _collectionsPromise = fetchAllCollections().catch(error => {
      _collectionsPromise = null;
      throw error;
    });
  }
  return _collectionsPromise;
}

/** Fire-and-forget prefetch — call on card hover so data is ready before click. */
export function prefetchProduct(handle: string): void {
  if (!handle || getCachedProduct(handle)) return;
  fetchProductByHandle(handle).catch(() => {/* silent */});
}

export async function createCheckout(
  items: Array<{
    variantId: string;
    quantity: number;
    attributes?: Array<{ key: string; value: string }>;
  }>,
  opts: {
    note?: string;
    cartAttributes?: Array<{ key: string; value: string }>;
    customerAccessToken?: string;
  } = {},
): Promise<string> {
  const res = await fetch(`${BASE}/api/shopify/cart/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items, ...opts }),
  });
  if (!res.ok) {
    const err = (await res.json()) as { error?: string };
    throw new Error(err.error ?? "Failed to create checkout");
  }
  const data = (await res.json()) as { checkoutUrl: string };
  return data.checkoutUrl;
}

/* ─── Delivery (lazy — only used by checkout page) ──────── */

export interface DraftDelivery {
  address: string;
  destinationLatitude?: number;
  destinationLongitude?: number;
  date: string;
  slot: string;
  origin: string;
  distanceKm: number;
  cost: number;
  recipientName?: string;
  recipientPhone?: string;
  contactConsent?: boolean;
  pickup?: boolean;
  courierNote?: string;
  shippingAddress: import('./shopifyApi.eager').ShippingAddress;
}

export async function calculateDeliveryCost(
  details: import('./shopifyApi.eager').DeliveryDetails,
): Promise<import('./shopifyApi.eager').DeliveryCostResult> {
  const res = await fetch(`${BASE}/api/delivery/cost`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(details),
  });
  const data = await res.json() as import('./shopifyApi.eager').DeliveryCostResult & { error?: string };
  if (!res.ok) throw new Error(data.error ?? 'Nie udało się obliczyć kosztu dostawy');
  return data;
}

/* ─── Customer orders ────────────────────────────────────── */

export interface OrderLineItem {
  title: string;
  quantity: number;
  originalTotalPrice: { amount: string; currencyCode: string };
  customAttributes: Array<{ key: string; value: string }>;
  variant: {
    title?: string | null;
    image: { url: string; altText: string | null } | null;
    price: { amount: string; currencyCode: string };
  } | null;
}

export interface ShopifyOrder {
  id: string;
  name: string;           // "#1001"
  orderNumber: number;
  processedAt: string;    // ISO date
  financialStatus: string;
  fulfillmentStatus: string;
  customAttributes: Array<{ key: string; value: string }>;
  currentTotalPrice: { amount: string; currencyCode: string };
  lineItems: { nodes: OrderLineItem[] };
}

export async function updateCustomer(
  accessToken: string | null,
  fields: { firstName?: string; lastName?: string; password?: string; phone?: string },
): Promise<{ id: string; firstName: string; lastName: string; email: string; displayName: string; phone: string | null }> {
  const res = await fetch(`${BASE}/api/shopify/customer/me`, {
    method: "PATCH",
    credentials: 'include',
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    body: JSON.stringify({ ...(accessToken ? { accessToken } : {}), ...fields }),
  });
  const data = (await res.json()) as {
    customer?: { id: string; firstName: string; lastName: string; email: string; displayName: string; phone: string | null };
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? "Aktualizacja nie powiodła się");
  return data.customer!;
}

export interface CustomerAddress {
  id: string;
  firstName: string;
  lastName: string;
  company: string | null;
  address1: string;
  address2: string | null;
  city: string;
  province: string;
  zip: string;
  country: string;
  phone: string | null;
}

export async function fetchCustomerOrders(
  token: string | null,
  limit = 20,
): Promise<ShopifyOrder[]> {
  const res = await fetch(
    `${BASE}/api/shopify/customer/orders?limit=${limit}`,
    { credentials: 'include', cache: 'no-store', headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!res.ok) {
    const err = (await res.json()) as { error?: string };
    throw new Error(err.error ?? "Failed to fetch orders");
  }
  const data = (await res.json()) as { orders: ShopifyOrder[] };
  return data.orders;
}

export interface AddressInput {
  firstName?: string;
  lastName?: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  province?: string;
  zip: string;
  country: string;
  phone?: string;
}

export interface CustomerAddressesResult {
  addresses: CustomerAddress[];
  defaultAddressId: string | null;
}

export async function fetchCustomerAddresses(
  tokenOrOAuth: { type: 'local'; token?: string | null } | { type: 'oauth' },
): Promise<CustomerAddressesResult> {
  if (tokenOrOAuth.type === 'oauth') {
    const res = await fetch(`${BASE}/api/auth/addresses`, { credentials: 'include', cache: 'no-store' });
    if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Failed to fetch addresses'); }
    return res.json() as Promise<CustomerAddressesResult>;
  }
  const res = await fetch(`${BASE}/api/shopify/customer/addresses`, {
    credentials: 'include', cache: 'no-store',
    headers: tokenOrOAuth.token ? { Authorization: `Bearer ${tokenOrOAuth.token}` } : {},
  });
  if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Failed to fetch addresses'); }
  return res.json() as Promise<CustomerAddressesResult>;
}

export async function createCustomerAddress(
  tokenOrOAuth: { type: 'local'; token?: string | null } | { type: 'oauth' },
  address: AddressInput,
  isDefault = false,
): Promise<{ id: string }> {
  if (tokenOrOAuth.type === 'oauth') {
    const res = await fetch(`${BASE}/api/auth/addresses`, {
      method: 'POST', credentials: 'include',
      // X-Requested-With: wymagane przez backend jako ochrona CSRF (patrz
      // artifacts/api-server/src/worker/csrf.ts) — zwykły <form> na obcej
      // stronie fizycznie nie może ustawić niestandardowego nagłówka.
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ address, defaultAddress: isDefault }),
    });
    if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Failed to create address'); }
    return res.json() as Promise<{ id: string }>;
  }
  const res = await fetch(`${BASE}/api/shopify/customer/addresses`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ ...(tokenOrOAuth.token ? { token: tokenOrOAuth.token } : {}), address }),
  });
  if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Failed to create address'); }
  return res.json() as Promise<{ id: string }>;
}

export async function updateCustomerAddress(
  tokenOrOAuth: { type: 'local'; token?: string | null } | { type: 'oauth' },
  id: string,
  address: AddressInput,
  isDefault = false,
): Promise<void> {
  if (tokenOrOAuth.type === 'oauth') {
    const res = await fetch(`${BASE}/api/auth/addresses/${encodeURIComponent(id)}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ address, defaultAddress: isDefault }),
    });
    if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Failed to update address'); }
    return;
  }
  const res = await fetch(`${BASE}/api/shopify/customer/addresses/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ ...(tokenOrOAuth.token ? { token: tokenOrOAuth.token } : {}), address }),
  });
  if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Failed to update address'); }
}

export interface CustomerConsents {
  emailMarketing: boolean;
  smsMarketing: boolean;
}

export async function deleteCustomerAddress(
  tokenOrOAuth: { type: 'local'; token?: string | null } | { type: 'oauth' },
  id: string,
): Promise<void> {
  if (tokenOrOAuth.type === 'oauth') {
    const res = await fetch(`${BASE}/api/auth/addresses/${encodeURIComponent(id)}`, {
      method: 'DELETE', credentials: 'include',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Failed to delete address'); }
    return;
  }
  const res = await fetch(`${BASE}/api/shopify/customer/addresses/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(tokenOrOAuth.token ? { token: tokenOrOAuth.token } : {}),
  });
  if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Failed to delete address'); }
}

export async function fetchCustomerConsents(
  tokenOrOAuth: { type: 'local'; token?: string | null } | { type: 'oauth' },
): Promise<CustomerConsents> {
  if (tokenOrOAuth.type === 'oauth') {
    const res = await fetch(`${BASE}/api/auth/consents`, { credentials: 'include', cache: 'no-store' });
    if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Failed to fetch consents'); }
    return res.json() as Promise<CustomerConsents>;
  }
  const res = await fetch(`${BASE}/api/shopify/customer/consents`, {
    credentials: 'include', cache: 'no-store',
    headers: tokenOrOAuth.token ? { Authorization: `Bearer ${tokenOrOAuth.token}` } : {},
  });
  if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Failed to fetch consents'); }
  return res.json() as Promise<CustomerConsents>;
}

export async function updateCustomerConsents(
  tokenOrOAuth: { type: 'local'; token?: string | null } | { type: 'oauth' },
  consents: Partial<CustomerConsents>,
): Promise<void> {
  if (tokenOrOAuth.type === 'oauth') {
    const res = await fetch(`${BASE}/api/auth/consents`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(consents),
    });
    if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Failed to update consents'); }
    return;
  }
  const res = await fetch(`${BASE}/api/shopify/customer/consents`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ ...(tokenOrOAuth.token ? { token: tokenOrOAuth.token } : {}), ...consents }),
  });
  if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Failed to update consents'); }
}

export async function setDefaultCustomerAddress(
  token: string | null,
  addressId: string,
): Promise<void> {
  const res = await fetch(`${BASE}/api/shopify/customer/addresses/${encodeURIComponent(addressId)}/default`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(token ? { token } : {}),
  });
  if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Failed to set default'); }
}
